<template>
  <v-container fluid class="ma-4 mt-3 px-8">
    <v-row dense align="center" class="mb-3">
      <v-col cols="12" md="2">
        <v-text-field
          v-model="filter.program_code"
          label="Program Code"
          outlined
          dense
          hide-details
          clearable
          @keyup.enter="loadEntries"
        />
      </v-col>
      <v-col cols="12" md="2">
        <v-text-field
          v-model="filter.course_code"
          label="Course Code"
          outlined
          dense
          hide-details
          clearable
          @keyup.enter="loadEntries"
        />
      </v-col>
      <v-col cols="auto">
        <v-btn
          class="accent white--text"
          :loading="loading"
          @click="loadEntries"
        >
          Search
        </v-btn>
      </v-col>
      <v-spacer />
            <v-btn
            v-if="!isReadOnly"
            class="mr-2"
              color="accent white--text"
              :disabled="isScheduled"
              @click="scheduleDialog = true"
            >
              <v-icon left>mdi-calendar-check</v-icon>
              {{ isScheduled ? "Scheduled" : "Schedule" }}
            </v-btn>
      <v-btn
        v-if="!isReadOnly"
        color="accent white--text mr-2"
        @click="uploadLinksDialog = true"
      >
        <v-icon left>mdi-upload</v-icon>
        Upload
      </v-btn>
      <v-btn color="accent white--text" @click="downloadEntries">
        <v-icon left>mdi-download</v-icon>
        Download
      </v-btn>
    </v-row>

    <v-sheet outlined>
      <v-data-table
        :headers="visibleHeaders"
        :items="entries"
        :loading="loading"
        fixed-header
        item-key="id"
      >
        <template v-slot:no-data>
          <div class="text-center">No entries found</div>
        </template>

        <template #[`item.webinar_date`]="{ item }">
          {{ formatDate(item.webinar_date) }}
        </template>

        <template #[`item.from_time`]="{ item }">
          {{ formatTime(item.from_time) }}
        </template>

        <template #[`item.to_time`]="{ item }">
          {{ formatTime(item.to_time) }}
        </template>

        <template #[`item.faculty_name`]="{ item }">
          <span v-if="item.faculty_name">{{ item.faculty_name }}</span>
          <span v-else class="grey--text">—</span>
        </template>

        <template #[`item.reschedule_status`]="{ item }">
          <v-chip v-if="!isScheduled" small color="blue-grey lighten-4">
            Not Scheduled
          </v-chip>
          <v-chip v-else-if="isPostponed(item)" small color="red lighten-4">
            Postponed
          </v-chip>
          <v-chip
            v-else-if="isRescheduled(item)"
            small
            color="orange lighten-4"
          >
            Rescheduled
          </v-chip>
          <v-chip v-else small color="grey lighten-3">Scheduled</v-chip>
        </template>

        <template #[`item.day`]="{ item }">
          <v-chip
            small
            :color="
              item.day === 'Friday' ? 'deep-purple lighten-4' : 'blue lighten-4'
            "
          >
            {{ item.day }}
          </v-chip>
        </template>

        <template #[`item.webinar_link`]="{ item }">
          <a
            v-if="item.webinar_link"
            :href="item.webinar_link"
            target="_blank"
            class="caption"
          >
            {{ item.webinar_link }}
          </a>
          <span v-else class="grey--text">—</span>
        </template>

        <template #[`item.slot`]="{ item }">
          {{ item.slot != null ? item.slot.toUpperCase() : "—" }}
        </template>

        <template #[`item.actions`]="{ item }">
          <v-tooltip top :disabled="!isEditLocked(item)">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-btn
                  icon
                  x-small
                  :disabled="isEditLocked(item)"
                  @click="openEditDialog(item)"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </span>
            </template>
            <span>Webinar completed and attendance already recorded</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-btn icon x-small @click="openDuplicateDialog(item)">
                  <v-icon small>mdi-content-duplicate</v-icon>
                </v-btn>
              </span>
            </template>
            <span>Duplicate</span>
          </v-tooltip>

          <v-tooltip top :disabled="isDeletable(item)">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-btn
                  icon
                  x-small
                  color="error"
                  :disabled="!isDeletable(item)"
                  @click="openDeleteDialog(item)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </span>
            </template>
            <span>Delete</span>
          </v-tooltip>
        </template>

        <template #[`item.attendance`]="{ item }">
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon x-small v-bind="attrs" v-on="on">
                <v-icon small>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="viewAttendanceReport(item)">
                <v-list-item-icon class="mr-2">
                  <v-icon small>mdi-chart-box-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>View Attendance Report</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-sheet>

    <v-dialog v-model="uploadLinksDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          <v-icon small color="secondary" class="mr-2">mdi-link-plus</v-icon>
          Upload Webinar
          <v-spacer />
          <v-btn small icon @click="closeUploadLinksDialog">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-5">
          <v-file-input
            v-model="linksFile"
            label="CSV File *"
            outlined
            dense
            hide-details="auto"
            accept=".csv"
            prepend-icon=""
            show-size
          />
          <div class="caption grey--text mt-3">
            Each row carries a single course code with one faculty name and one
            webinar link. If a session is shared by two courses, upload it once
            and use <strong>Duplicate</strong> on the entry to add the second
            course code.
            <div class="mt-1">Do not use a comma inside any value.</div>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-4 pb-4 pt-3">
          <v-spacer />
          <v-btn
            class="accent white--text"
            :loading="uploadingLinks"
            :disabled="!linksFile"
            @click="submitLinks"
          >
            <v-icon left>mdi-upload</v-icon>
            Upload
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <EditEntryDialog
      v-model="editDialog"
      :item="editItem"
      :min-date="minStartDate"
      :scheduled="isScheduled"
      @saved="loadEntries"
    />

    <DuplicateEntryDialog
      v-model="duplicateDialog"
      :item="duplicateItem"
      @saved="loadEntries"
    />

    <v-dialog v-model="scheduleDialog" width="500" persistent>
      <confirmationDialog
        v-if="scheduleDialog"
        confirmationText="Are you sure you want to schedule the entries for webinar?"
        :loading="scheduling"
        @closeDialog="scheduleDialog = false"
        @saveDialog="confirmSchedule"
      />
    </v-dialog>

    <v-dialog v-model="deleteDialog" width="500" persistent>
      <confirmationDialog
        v-if="deleteDialog"
        confirmationText="Are you sure you want to delete this webinar schedule?"
        :loading="deleting"
        @closeDialog="deleteDialog = false"
        @saveDialog="confirmDelete"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import EditEntryDialog from "./EditEntryDialog.vue";
import DuplicateEntryDialog from "./DuplicateEntryDialog.vue";
import confirmationDialog from "@/components/confirmationDialog.vue";

export default {
  components: { EditEntryDialog, DuplicateEntryDialog, confirmationDialog },
  data() {
    return {
      loading: false,
      run: null,
      entries: [],
      filter: { program_code: "", course_code: "" },

      uploadLinksDialog: false,
      uploadingLinks: false,
      linksFile: null,

      editDialog: false,
      editItem: null,

      duplicateDialog: false,
      duplicateItem: null,

      deleteDialog: false,
      deleting: false,
      deleteItem: null,

      scheduleDialog: false,
      scheduling: false,

      headers: [
        { text: "Programme", value: "program_code" },
        { text: "Semester", value: "semester_number" },
        { text: "Class ID", value: "class_id" },
        { text: "Course", value: "course_code" },
        { text: "Faculty", value: "faculty_name" },
        { text: "Day", value: "day" },
        { text: "Webinar Date", value: "webinar_date" },
        { text: "Slot", value: "slot" },
        { text: "From Time", value: "from_time" },
        { text: "To Time", value: "to_time" },
        { text: "Status", value: "reschedule_status", sortable: false },
        { text: "Webinar Link", value: "webinar_link" },
        { text: "Actions", value: "actions", sortable: false },
        { text: "Attendance", value: "attendance", sortable: false },
      ],
    };
  },

  computed: {
    isReadOnly() {
      return this.$store.state.currentUser?.role === "Attendance Verifier";
    },
    visibleHeaders() {
      if (!this.isReadOnly) return this.headers;
      return this.headers.filter((h) => h.value !== "actions");
    },
    isScheduled() {
      return Boolean(this.run?.is_scheduled);
    },
    minStartDate() {
      return this.currentDate(new Date());
    },
    runId() {
      return this.$route.params.runId;
    },
  },

  async created() {
    this.$store.commit("setBreadcrumbs", [
      { text: "Webinar Scheduler", to: { name: "Webinar Scheduler" } },
      { text: "Schedules" },
    ]);
    await Promise.all([this.loadRun(), this.loadEntries()]);
  },

  methods: {
    currentDate(date) {
      const d = new Date(date);
      let month = "" + (d.getMonth() + 1);
      let day = "" + d.getDate();
      const year = d.getFullYear();

      if (month.length < 2) month = "0" + month;
      if (day.length < 2) day = "0" + day;

      return [year, month, day].join("-");
    },
    async loadRun() {
      try {
        const res = await this.$axios.get(`/scheduler/runs/${this.runId}`);
        this.run = res.data.data || res.data;
      } catch (e) {
        console.log(e);
      }
    },

    async loadEntries() {
      this.loading = true;
      try {
        const query = new URLSearchParams();
        if (this.filter.program_code) {
          query.append("program_code", this.filter.program_code);
        }
        if (this.filter.course_code) {
          query.append("course_code", this.filter.course_code);
        }
        const params = query.toString() ? `?${query.toString()}` : "";
        const res = await this.$axios.get(
          `/scheduler/runs/${this.runId}/entries${params}`,
        );
        this.entries = res.data.data || [];
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },

    async submitLinks() {
      this.uploadingLinks = true;
      try {
        const fd = new FormData();
        fd.append("file", this.linksFile);
        await this.$axios.post(
          `/scheduler/runs/${this.runId}/upload_links`,
          fd,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
        this.$store.commit("showSnackbar", {
          message: "Links uploaded successfully",
          color: "success",
        });
        this.closeUploadLinksDialog();
        await this.loadEntries();
      } catch (e) {
        const msg =
          e.response?.data?.error ||
          e.response?.data?.message ||
          "Upload failed";
        this.$store.commit("showSnackbar", { message: msg, color: "error" });
      } finally {
        this.uploadingLinks = false;
      }
    },

    closeUploadLinksDialog() {
      this.uploadLinksDialog = false;
      this.linksFile = null;
    },

    openEditDialog(item) {
      this.editItem = { ...item };
      this.editDialog = true;
    },

    openDuplicateDialog(item) {
      this.duplicateItem = { ...item };
      this.duplicateDialog = true;
    },

    isEditLocked(item) {
      return this.isCompleted(item) && Boolean(item.attendance_marked);
    },

    isDeletable(item) {
      return this.isUpcomingDate(item) && !item.attendance_marked;
    },

    isUpcomingDate(item) {
      if (!item.webinar_date) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const [y, m, d] = item.webinar_date.split("-").map(Number);
      // Today still counts as upcoming — a date is only past once it has elapsed.
      return new Date(y, m - 1, d) >= today;
    },

    openDeleteDialog(item) {
      this.deleteItem = item;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      if (!this.deleteItem) return;
      this.deleting = true;
      try {
        await this.$axios.delete(`/scheduler/entries/${this.deleteItem.id}`);
        this.$store.commit("showSnackbar", {
          message: "Webinar schedule deleted",
          color: "success",
        });
        this.deleteDialog = false;
        this.deleteItem = null;
        await this.loadEntries();
      } catch (e) {
        const msg =
          e.response?.data?.error ||
          e.response?.data?.message ||
          "Delete failed";
        this.$store.commit("showSnackbar", { message: msg, color: "error" });
      } finally {
        this.deleting = false;
      }
    },

    async confirmSchedule() {
      this.scheduling = true;
      try {
        await this.$axios.post(`/scheduler/runs/${this.runId}/schedule`);
        this.$store.commit("showSnackbar", {
          message: "Webinars scheduled",
          color: "success",
        });
        this.scheduleDialog = false;
        await Promise.all([this.loadRun(), this.loadEntries()]);
      } catch (e) {
        const msg =
          e.response?.data?.error ||
          e.response?.data?.message ||
          "Scheduling failed";
        this.$store.commit("showSnackbar", { message: msg, color: "error" });
      } finally {
        this.scheduling = false;
      }
    },

    isCompleted(item) {
      if (!item.webinar_date) return false;
      const [y, m, d] = item.webinar_date.split("-").map(Number);
      const time = item.to_time || item.from_time;
      let hh = 23;
      let mm = 59;
      if (time) {
        const [h, mi] = String(time).split(":");
        hh = Number(h);
        mm = Number(mi);
      }
      const end = new Date(y, m - 1, d, hh, mm, 0);
      return end < new Date();
    },

    isRescheduled(item) {
      return Boolean(
        item.rescheduled ||
          item.is_rescheduled ||
          item.isRescheduled ||
          item.reschedule_status === "rescheduled" ||
          item.status === "rescheduled",
      );
    },

    isPostponed(item) {
      return Boolean(
        item.is_postponed ||
          item.isPostponed ||
          item.postponed ||
          item.reschedule_status === "postponed" ||
          item.status === "postponed",
      );
    },

    downloadEntries() {
      if (!this.entries.length) return;
      const headers =
        "program_code,semester_number,class_id,course_code,day,weekend,webinar_date,slot,faculty_name,from_time,to_time,webinar_link";
      const rows = this.entries.map((e) =>
        [
          e.program_code,
          e.semester_number,
          e.class_id,
          e.course_code,
          e.day,
          e.weekend,
          e.webinar_date,
          e.slot || "",
          e.faculty_name || "",
          e.from_time
            ? String(e.from_time).split(":").slice(0, 2).join(":")
            : "",
          e.to_time ? String(e.to_time).split(":").slice(0, 2).join(":") : "",
          e.webinar_link || "",
        ].join(","),
      );
      const csv = [headers, ...rows].join("\n");
      const name = `webinar_schedule_run${this.runId}${
        this.filter.program_code ? `_${this.filter.program_code}` : ""
      }${this.filter.course_code ? `_${this.filter.course_code}` : ""}.csv`;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    },

    formatDate(dateStr) {
      if (!dateStr) return "—";
      const [y, m, d] = dateStr.split("-");
      return `${d}-${m}-${y}`;
    },

    formatTime(timeStr) {
      if (!timeStr) return "—";

      return timeStr.split(":").slice(0, 2).join(":");
    },

    downloadAttendanceTemplate() {
      const headers = "usn_id,status";
      const sample = ["123456,Present"];
      const csv = [headers, ...sample].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "attendance_template.csv";
      a.click();
      URL.revokeObjectURL(url);
    },
    viewAttendanceReport(item) {
      this.$router.push({
        name: "Attendance Report",
        params: { entryId: item.id },
      });
    },
  },
};
</script>
